import { Injectable, inject } from '@angular/core';
import {
  CoreRouterActionGoBack,
  CoreRouterActions,
  selectorCoreAppVersion,
  selectorCoreLogin,
  selectorCoreMeasureToTake,
  selectorCoreSelectedInstance
} from '@bepatient/core';
import { ToastController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { TakeMeasureApiService } from './take-measure-api.service';
import {
  TakeMeasureActionLoadMeasuresListFailed,
  TakeMeasureActionLoadMeasuresListSuccess,
  TakeMeasureActions,
  TakeMeasureActionSaveManualMeasure,
  TakeMeasureActionSaveManualMeasureFailed,
  TakeMeasureActionSaveManualMeasureSuccess,
  TakeMeasureActionTypes
} from './take-measure.actions';

@Injectable()
export class TakeMeasureEffects {
  private store = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private translate = inject(TranslateService);
  private toastController = inject(ToastController);
  private takeMeasureApiService = inject(TakeMeasureApiService);

  loadMeasuresList$: Observable<TakeMeasureActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(TakeMeasureActionTypes.LOAD_MEASURES_LIST),
      switchMap(() =>
        this.takeMeasureApiService.loadMeasuresList().pipe(
          map(res => new TakeMeasureActionLoadMeasuresListSuccess(res)),
          catchError(() => of(new TakeMeasureActionLoadMeasuresListFailed()))
        )
      )
    )
  );

  saveManualMeasure$: Observable<TakeMeasureActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(TakeMeasureActionTypes.SAVE_MANUAL_MEASURE),
      map((action: TakeMeasureActionSaveManualMeasure) => action.payload),
      withLatestFrom(
        this.store.pipe(select(selectorCoreMeasureToTake)),
        this.store.pipe(select(selectorCoreAppVersion)),
        this.store.pipe(
          select(selectorCoreSelectedInstance),
          map(selectedInstance => selectedInstance && selectedInstance.code)
        ),
        this.store.pipe(
          select(selectorCoreLogin),
          map(login => login && login.userId)
        )
      ),
      switchMap(([measureData, measure, appVersion, instanceCode, userId]) =>
        this.takeMeasureApiService
          .saveManualMeasure({
            measure,
            measureData,
            userId,
            appVersion,
            instanceCode
          })
          .pipe(
            map(() => new TakeMeasureActionSaveManualMeasureSuccess()),
            catchError(() => of(new TakeMeasureActionSaveManualMeasureFailed()))
          )
      )
    )
  );

  saveManualMeasureSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(TakeMeasureActionTypes.SAVE_MANUAL_MEASURE_SUCCESS),
      map(() => new CoreRouterActionGoBack())
    )
  );

  saveManualMeasureFailedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TakeMeasureActionTypes.SAVE_MANUAL_MEASURE_FAILED),
        tap(() => {
          this.toastController
            .create({
              message: this.translate.instant('SAVE_FAILED'),
              position: 'top',
              duration: 2000,
              cssClass: 'bepatient_dynamic_form_save_toast'
            })
            .then(toast => toast.present());
        })
      ),
    { dispatch: false }
  );

  saveManualMeasureSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TakeMeasureActionTypes.SAVE_MANUAL_MEASURE_SUCCESS),
        delay(500),
        tap(() => {
          this.toastController
            .create({
              message: this.translate.instant('TAKE_MEASURE.SAVE_SUCCESS'),
              position: 'top',
              duration: 2000,
              cssClass: 'bepatient_dynamic_form_save_toast'
            })
            .then(toast => toast.present());
        })
      ),
    { dispatch: false }
  );
}
